import React, { useState, useCallback, Children, cloneElement, useMemo, useRef, useImperativeHandle, forwardRef } from 'react';
import {
  Button, Menu, Divider,
} from '@mui/material';
import { ArrowDropDown as DropDownIcon } from '@mui/icons-material';
import styled from 'styled-components';
import { CloseButton } from 'shared/components/styled';
import MenuItem from 'shared/components/MenuItem';

export default forwardRef(DropDownMenu);

const anchor = {
  vertical: 'bottom',
  horizontal: 'right',
};
const transform = {
  vertical: 'top',
  horizontal: 'right',
};

function DropDownMenu({
  buttonTitle,
  menuItems,
  onMenuItemClick,
  children,
  keepOpen,
  hasCloseButton,
  anchorOrigin = anchor,
  transformOrigin = transform,
  minMenuWidth = 120,
  menuComponent = Menu,
  width,
  ...other
}, ref) {
  const [open, setOpen] = useState();
  const handleClick = useCallback(event => {
    setOpen(true);
  }, []);
  const handleMenuClose = useCallback(event => {
    if (!keepOpen || event?.target?.getAttribute('aria-hidden')) {
      setOpen(false);
    }
  }, [keepOpen]);
  const handleCloseButton = useCallback(() => {
    setOpen(false);
  });
  const MenuComponent = menuComponent;
  const menuPtrops = useMemo(() => minMenuWidth ?? width
    ? { style: { minWidth: Math.max(minMenuWidth, width) } }
    : undefined
    , [minMenuWidth, width]);
  const style = useMemo(() => ({
    maxWidth: width,
  }), [width]);
  const button = useRef();
  useImperativeHandle(ref, () => ({
    close() {
      setOpen(false);
    }
  }));

  return (
    <>
      <DropDownButton
        endIcon={<DropDownIcon />}
        onClick={handleClick}
        {...other}
        size="small"
        ref={button}
      >
        {buttonTitle}
      </DropDownButton>
      <MenuComponent
        anchorEl={button.current}
        getContentAnchorEl={null}
        onClose={handleMenuClose}
        PaperProps={menuPtrops}
        ref={ref}
        open={open}
        anchorOrigin={anchorOrigin}
        transformOrigin={transformOrigin}
        style={style}
      >
        {open && (<>
          {hasCloseButton && <CloseButton onClick={handleCloseButton} />}
          {menuItems && menuItems
            .map(({ id, value, label }) => (label === '-'
              ? <Divider key={id} />
              : <MenuItem
                key={id}
                onClick={event => {
                  onMenuItemClick(id ?? value, event);
                  handleMenuClose();
                }}
              >
                {label}
              </MenuItem>
            ))
          }
          {Children.map(children, child =>
            child && cloneElement(child, {
              ...child.props,
              onClick: event => {
                const { onClick } = child.props;
                if (onClick) {
                  onClick(event);
                }
                handleMenuClose();
              },
            })
          )}
        </>)}
      </MenuComponent>
    </>
  );
}

const DropDownButton = styled(Button)`
  text-transform: none !important;
  font-weight: bold !important;
  font-size: 16px;
`;
